import { createContext, ReactNode, useContext, useEffect, useMemo } from "react"

import { XMarkIcon } from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"
import invariant from "tiny-invariant"

type ModalContextProps = { onClose?: () => void }
const ModalContext = createContext<ModalContextProps | null>(null)

export type ModalProps = {
  children: ReactNode
  className?: string
  backgroundClassName?: string
  onClose?: () => void
}

export type ModalHeaderProps = {
  className?: string
  children?: ReactNode
}

export function useModalContext(): ModalContextProps {
  const context = useContext(ModalContext)

  invariant(context, "useModalContext must be used within the ModalContext provider!")

  return context
}

export function Modal({ onClose, children, className, backgroundClassName }: ModalProps) {
  useEffect(() => {
    const initialBodyOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose?.()
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => {
      document.body.style.overflow = initialBodyOverflow
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [])

  const value = useMemo(() => ({ onClose }), [onClose])

  return (
    <ModalContext value={value}>
      <div
        className={twMerge(
          "fixed top-0 right-0 left-0 z-50 flex h-screen w-screen place-content-center bg-gray-600/80 backdrop-blur-sm",
          backgroundClassName
        )}
        onClick={onClose}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className={twMerge(
            "m-6 flex max-h-[calc(100vh-3rem)] max-w-screen flex-col place-self-center overflow-hidden rounded-lg bg-white",
            className
          )}
        >
          {children}
        </div>
      </div>
    </ModalContext>
  )
}

export function ModalHeader({ className, children }: ModalHeaderProps) {
  return <div className={twMerge("w-full border-b border-gray-200", className)}>{children}</div>
}

export function ModalCloseButton() {
  const { onClose } = useModalContext()
  return (
    <button type="button" onClick={onClose} className="cursor-pointer p-2 text-gray-900">
      <XMarkIcon className="h-5 w-5" />
    </button>
  )
}
